import { useEffect, useState } from "react";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { ShoppingBag, User, LayoutDashboard, Menu, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { id: "coleccion", label: "Colección", testid: "nav-catalog-link", category: null },
  { id: "coleccion", label: "Hombre", testid: "nav-hombre-link", category: "hombre" },
  { id: "coleccion", label: "Mujer", testid: "nav-mujer-link", category: "mujer" },
  { id: "contacto", label: "Contacto", testid: "nav-contact-link", category: null },
];

export const Navbar = () => {
  const { user } = useAuth();
  const { count, setCartOpen, justAdded } = useCart();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id, category = null) => {
    setMenuOpen(false);
    const query = category ? `?category=${category}` : "";
    if (window.location.pathname !== "/" || (category && !window.location.search.includes(category))) {
      navigate("/" + query);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 500);
    } else {
      if (category === null && window.location.search) {
        navigate("/");
      }
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.div
        style={{ scaleX: progress }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-gold-dark via-gold to-gold-light z-50 origin-left"
        data-testid="scroll-progress-bar"
      />
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-40 backdrop-blur-xl border-b transition-[background-color,border-color] duration-500 ${scrolled ? "bg-black/85 border-gold/25" : "bg-black/60 border-white/10"}`}
        data-testid="main-navbar"
      >
        <div className={`max-w-7xl mx-auto px-6 grid grid-cols-3 items-center transition-[height] duration-500 ${scrolled ? "h-16" : "h-20"}`}>
          <nav className="hidden md:flex items-center gap-8 text-[11px] uppercase tracking-[0.25em] text-neutral-400">
            {LINKS.map((l) => (
              <button key={l.label} data-testid={l.testid} onClick={() => scrollTo(l.id, l.category)} className="relative group hover:text-gold transition-colors duration-300 py-1">
                {l.label}
                <span className="absolute left-0 -bottom-0.5 w-full h-px bg-gold scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
              </button>
            ))}
          </nav>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMenuOpen((o) => !o)}
            className="md:hidden text-neutral-300 hover:text-gold transition-colors"
            aria-label="Abrir menú"
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

        <Link to="/" data-testid="nav-logo" className="justify-self-center text-center group">
          <span className="font-serif-lux text-2xl tracking-[0.35em] text-white group-hover:text-gold transition-colors duration-500">MÉ</span>
          <span className="block text-[8px] uppercase tracking-[0.5em] text-neutral-500 mt-0.5">Maison Élixir</span>
        </Link>

        <div className="justify-self-end flex items-center gap-4">
          {user?.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin-button"
              className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] border border-gold/50 text-gold px-4 py-2 hover:bg-gold hover:text-black transition-colors duration-300"
            >
              <LayoutDashboard size={14} /> Admin
            </Link>
          )}
          {user?.role === "customer" && (
            <Link
              to="/mis-pedidos"
              data-testid="nav-customer-button"
              className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-gold transition-colors duration-300"
            >
              <User size={14} /> Mi Cuenta
            </Link>
          )}
          {!user && (
            <Link to="/login" data-testid="nav-login-button" className="text-neutral-500 hover:text-gold transition-colors duration-300" aria-label="Acceso a mi cuenta">
              <User size={18} />
            </Link>
          )}
          <motion.button
            data-testid="nav-cart-button"
            onClick={() => setCartOpen(true)}
            animate={justAdded ? { scale: [1, 1.35, 1], rotate: [0, -10, 10, 0] } : { scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className={`relative transition-colors duration-300 ${justAdded ? "text-gold shadow-gold/50" : "text-white hover:text-gold"}`}
            aria-label="Abrir cesta"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <motion.span
                key={count}
                initial={{ scale: 0 }}
                animate={{ scale: justAdded ? 1.4 : 1 }}
                transition={{ duration: 0.3 }}
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 bg-gold text-black text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center shadow-lg"
              >
                {count}
              </motion.span>
            )}
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-black/95"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-8 py-6 gap-5 text-xs uppercase tracking-[0.3em] text-neutral-300">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.35 }}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => scrollTo(l.id, l.category)}
                  className="text-left hover:text-gold transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
    </>
  );
};
